import { Alert, Button, Card, Input, Spin, TableProps } from "antd";
import { capitalize } from "lodash";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast as toastMessage } from "react-toastify";
import * as XLSX from "xlsx";
import { useAppSelector } from "../../../../app/store";
import AddIcon from "../../../../assets/icons/AddIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import AvatarImage from "../../../../assets/images/empty-without-text.svg";
import ConfirmActionModal from "../../../../components/Businesses/ConfirmActionModal";
import BalanceSlide from "../../../../components/common/BalanceSlide";
import SuccessModal from "../../../../components/common/SuccessModal";
import CustomTable from "../../../../components/ui/CustomTable";
import Loader from "../../../../components/ui/Loader";
import {
  useGetSingleBatchQuery,
  useGetUserPerformBatchQuery,
  useUpdateBatchMutation,
} from "../../../../features/portal/batch/batchApi";
import { useGetSingleCompanyQuery } from "../../../../features/portal/companyOnboard/companyOnboardApi";
import {
  useBatchRejectVendorMutation,
  useCheckButtonForVendorPaymentsStatusQuery,
  useGetAllVendorsQuery,
  useSentToCheckerVendorMutation,
  useVendorPaymentsSentToCheckerMutation,
  useVendorsPaymentWithOtpMutation,
} from "../../../../features/portal/disbursementPayment/disbursementPaymentApi";
import { useGetUserPoolProjectQuery } from "../../../../features/portal/Products/productsApi";
import PinVerifyModal from "../../../../features/portal/vendorPayment/PinVerifyModal";
import { useBalanceCheckQuery } from "../../../../features/portal/vendorPayment/vendorPaymentApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import ConfirmModalLayout from "../../../../layouts/ConfirmModalLayout";
import { formatDate, formatTime } from "../../../../lib/time";
import {
  GetAllDisbursementDataParamsType,
  GetAllVendorPaymentDataType,
} from "../../../../types/portal/disbursementPayment";
import {
  addCommaInNumber,
  countParPageSerialNumber,
} from "../../../../utils/number";
import { addKeyInArray } from "../../../../utils/utils";
import AddWalletModal from "../../ShebaPayWallet/AddWalletModal";

export default function PendingListTableVendor() {
  const [selectedData, setSelectedData] = useState<string[]>([]);
  const [paymentStatus, setPayentStatus] = useState("true");
  const [comment, setComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const {
    open: addWalletOpen,
    handleOpen: addWalletHandleOpen,
    handleClose: addWalletHandleClose,
  } = useModal();
  const {
    open: openConfirmSubmitModal,
    handleOpen: handleConfirmSubmitOpen,
    handleClose: handleConfirmSubmitClose,
  } = useModal<undefined, "CONFIRM_SUBMIT">();
  const {
    open: openConfirmRejectModal,
    handleOpen: handleConfirmRejectOpen,
    handleClose: handleConfirmRejectClose,
  } = useModal<undefined, "CONFIRM_REJECT">();
  const {
    open: openConfirmPaymentModal,
    handleOpen: handleConfirmPaymentOpen,
    handleClose: handleConfirmPaymentClose,
  } = useModal<undefined, "CONFIRM_PAYMENT">();
  const {
    open: openConfirmMackerModal,
    handleOpen: handleConfirmMackerOpen,
    handleClose: handleConfirmMackerClose,
  } = useModal<undefined, "CONFIRM_SUBMIT_MACKER">();

  const {
    open: openSuccessModal,
    handleOpen: handleSuccessOpen,
    handleClose: handleSuccessClose,
  } = useModal<undefined, "SUCCESS">();
  const { open, handleOpen, handleClose } = useModal<
    undefined,
    "CONFIRM_FOR_APPROVAL"
  >();

  const {
    page,
    limit,
    handlePagination,
    handleFilter,
    handleLimit,
    filter,
    keyword,
    handleSearch,
  } = useTable<GetAllDisbursementDataParamsType>({
    defaultLimit: 1000,
  });
  const { user, role } = useAppSelector((state) => state.auth);
  const { id } = useParams();
  const roleCheck = localStorage.getItem("project_role");

  const {
    isLoading: isCompanyDetailsLoading,
    isError: isCompanyDetailsError,
    error: companyDetailsError,
    data: companyDetails,
  } = useGetSingleCompanyQuery(user?.data?.company_id, {
    skip: !user?.data?.company_id,
  });
  const {
    isLoading: isBalanceCheckLoading,
    isError: isBalanceCheckError,
    error: balanceCheckError,
    data: balanceCheck,
  } = useBalanceCheckQuery(
    {
      walletNumber: companyDetails?.data?.wallet_number || "",
      companyId: user?.data?.company_id,
    },
    {
      skip: !companyDetails?.data?.wallet_number || !user?.data?.company_id,
    }
  );

  const {
    isLoading: isVendorLoading,
    isError: isVendorDetailsError,
    error: vendorDetailsError,
    data: vendorDetails,
    isFetching,
    refetch,
  } = useGetAllVendorsQuery({
    params: {
      ...filter,
      batch_id: id,
      company_id: user?.data?.company_id,
      permission: roleCheck?.toLocaleLowerCase(),
      page,
      limit,
      search: keyword,
    },
  });
  const [
    sentToCheckerVendor,
    {
      isLoading: sentToCheckerIsLoading,
      isError: sentToCheckerIsError,
      error: sentToCheckerError,
      isSuccess: sentToCheckerIsSuccess,
    },
  ] = useSentToCheckerVendorMutation();
  const {
    isError: singleBatchIsError,
    error: singleBatchError,
    data: singleBatchData,
    isLoading: singleBatchIsLoading,
  } = useGetSingleBatchQuery(id);
  const shouldFetchUserPoolProject =
    roleCheck === "Approver" && !!singleBatchData?.data?.projectId;
  const {
    isError: isUserPoolProjectError,
    error: userPoolProjectError,
    data: userPoolProjectData,
  } = useGetUserPoolProjectQuery(
    {
      params: {
        userId: user?.data?.id,
        projectId: singleBatchData?.data?.projectId,
      },
    },
    {
      skip: !shouldFetchUserPoolProject,
    }
  );
  const [
    updateBatch,
    {
      isError: isUpdateBatchError,
      error: updateBatchError,
      isSuccess: isUpdateBatchSuccess,
      isLoading: isUpdateBatchLoading,
    },
  ] = useUpdateBatchMutation();

  const {
    isError: isUserPerformBatchError,
    error: userPerformBatchError,
    data: userPerformBatchData,
  } = useGetUserPerformBatchQuery(
    {
      params: {
        userId: user?.data?.id,
        batchId: id,
      },
    },
    {
      skip: !user?.data?.id,
    }
  );

  const [
    batchRejectVendor,
    {
      isSuccess: batchRejectIsSuccess,
      isLoading: batchRejectIsLoading,
      isError: batchRejectIsError,
      error: batchRejectError,
    },
  ] = useBatchRejectVendorMutation();

  useError(isVendorDetailsError, vendorDetailsError, "Get payment data fail");
  useError(
    isUserPoolProjectError,
    userPoolProjectError,
    "Get user pool project fail"
  );
  const [
    vendorPaymentsSentToChecker,
    {
      isError: isPaymentCheckerError,
      error: paymentCheckerError,
      isSuccess: isPaymentCheckerSuccess,
      isLoading: paymentCheckerIsLoading,
    },
  ] = useVendorPaymentsSentToCheckerMutation();
  const [
    vendorsPaymentWithOtp,
    {
      isError: isSalaryPaymentError,
      error: salaryPaymentError,
      isLoading: isSalaryPaymentLoading,
      isSuccess: isSalaryPaymentSuccess,
      data: salaryPaymentData,
    },
  ] = useVendorsPaymentWithOtpMutation();
  const { data: CheckButtonForVendorPaymentsStatusData } =
    useCheckButtonForVendorPaymentsStatusQuery({
      params: {
        batch_id: id ? Number(id) : undefined,
        current_stage: roleCheck ?? undefined,
        status: "pending",
      },
    });
  const { data: CheckButtonForVendorPaymentsStatusRejectData } =
    useCheckButtonForVendorPaymentsStatusQuery({
      params: {
        batch_id: id ? Number(id) : undefined,
        current_stage: roleCheck ?? undefined,
        status: "reject",
      },
    });
  useError(isCompanyDetailsError, companyDetailsError, "Get company data fail");
  useError(isBalanceCheckError, balanceCheckError, "Get balance data fail");
  useError(sentToCheckerIsError, sentToCheckerError, "Sent to checkeer fail");
  useError(singleBatchIsError, singleBatchError, "Get single batch fail");
  useError(isUpdateBatchError, updateBatchError, "Batch update fail");
  useError(isPaymentCheckerError, paymentCheckerError, "Send to approver fail");
  useError(batchRejectIsError, batchRejectError, "Batch reject fail");

  const columns: TableProps<GetAllVendorPaymentDataType>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: <div className="whitespace-nowrap !w-[120px]">Vendor ID</div>,
      dataIndex: "vendor_id",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Vendor Name</span>,
      dataIndex: "vendor_name",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Invoice No</span>,
      dataIndex: "invoiceNo",
      render: (val) => (val ? val : "N/A"),
    },
    // {
    //   title: <span className="whitespace-nowrap">Department</span>,
    //   align: "center",
    //   dataIndex: "department",
    //   render: (val) => (val ? val : "N/A"),
    // },
    // {
    //   title: <span className="whitespace-nowrap">Designation</span>,
    //   dataIndex: "designation",
    //   render: (val) => (val ? val : "N/A"),
    // },
    {
      title: <span className="whitespace-nowrap">Amount (BDT)</span>,
      dataIndex: "amount",
      align: "center",
      render: (val: string | number) => (
        <>
          ৳{" "}
          {val
            ? Number(val)
                .toFixed(2)
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            : "0"}
        </>
      ),
    },
    {
      title: <span className="whitespace-nowrap">Wallet Number</span>,
      dataIndex: "wallet_name",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Bank Name</span>,
      dataIndex: "bank_name",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Account Title</span>,
      dataIndex: "account_title",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Account Number</span>,
      dataIndex: "account_number",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Branch Name</span>,
      dataIndex: "branch_name",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Routing Number</span>,
      dataIndex: "routing_number",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Transaction ID</span>,
      dataIndex: "transaction_id",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Transaction Date</span>,
      dataIndex: "transaction_date",
      render: (val: string) =>
        val ? (
          <>
            {formatDate(val)}, {formatTime(val)}
          </>
        ) : (
          "N/A"
        ),
    },
    {
      title: <span className="whitespace-nowrap">Created At</span>,
      align: "center",
      dataIndex: "created_at",
      render: (val: string) =>
        val ? (
          <>
            {formatDate(val)}, {formatTime(val)}
          </>
        ) : (
          "N/A"
        ),
    },
    {
      title: <span className="whitespace-nowrap">Updated At</span>,
      align: "center",
      dataIndex: "updated_at",
      render: (val: string) =>
        val ? (
          <>
            {formatDate(val)}, {formatTime(val)}
          </>
        ) : (
          "N/A"
        ),
    },
    {
      title: <span className="whitespace-nowrap">Narration</span>,
      dataIndex: "narration",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <div className="whitespace-nowrap !w-[110px]">Current Stage</div>,
      dataIndex: "current_stage",
      align: "center",
      render: (val) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-700`}
        >
          {val.charAt(0).toUpperCase() + val.slice(1)}
        </span>
      ),
    },
    {
      title: <div className="whitespace-nowrap !w-[110px]">Status</div>,
      dataIndex: "status",
      align: "center",
      render: (val) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            val === "Pending"
              ? "bg-yellow-100 text-yellow-700"
              : "bg-green-100 text-green-700"
          }`}
        >
          {val || "" ? capitalize(val) : "N/A"}
        </span>
      ),
    },
  ];

  const singleBatch = singleBatchData?.data;

  // const handlePoolChecked = () => {
  //   console.log("roleCheck", roleCheck);

  //   if (roleCheck !== "Checker") {
  //     return null;
  //   }

  //   console.log("Status", singleBatchData?.data?.status);

  //   if (singleBatchData?.data?.status !== "checker") {
  //     return null;
  //   }

  //   const poolName = userPoolProjectData?.data?.poolName;

  //   console.log("poolName", poolName);

  //   if (poolName === "Pool A" && singleBatchData?.data?.numberOfPollA) {
  //     if (
  //       singleBatchData?.data?.numberOfPollA >
  //       Number(singleBatchData?.data?.numberOfPoolACheck)
  //     ) {
  //       // send to approve korte parbe
  //       return "CHECKE";
  //     }
  //     return "CHECKE";
  //   } else if (poolName === "Pool B" && singleBatchData?.data?.numberOfPollB) {
  //     if (
  //       singleBatchData?.data?.numberOfPollB >
  //       Number(singleBatchData?.data?.numberOfPoolBCheck)
  //     ) {
  //       // send to approve korte parbe
  //       return "CHECKE";
  //     }
  //     return null;
  //   }
  //   return null;
  // };

  // console.log("function", handlePoolChecked());

  // const handleChecked = () => {
  //   const totalPool =
  //     (singleBatchData?.data?.numberOfPollA || 0) +
  //     (singleBatchData?.data?.numberOfPollB || 0);
  //   const totalPoolChecke =
  //     (singleBatchData?.data?.numberOfPoolACheck || 0) +
  //     (singleBatchData?.data?.numberOfPoolBCheck || 0);
  //   if (totalPoolChecke === totalPool - 1) {
  //     return "SUBMIT";
  //   } else if (totalPoolChecke < totalPool) {
  //     return "CHECK";
  //   } else {
  //     return "APPROVE";
  //   }
  // };

  // const handleSendToCheck = async (value: string) => {
  //   const poolName = userPoolProjectData?.data?.poolName;
  //   let obj = {};
  //   if (poolName === "Pool A") {
  //     obj = {
  //       numberOfPoolACheck: (singleBatch?.numberOfPoolACheck || 0) + 1,
  //     };
  //   }
  //   if (poolName === "Pool B") {
  //     obj = {
  //       numberOfPoolBCheck: (singleBatch?.numberOfPoolBCheck || 0) + 1,
  //     };
  //   }

  //   if (handleChecked() === "SUBMIT") {
  //     if (!selectedData?.length) {
  //       toastMessage.error("Please select a user");
  //       return;
  //     }

  //     updateBatch({
  //       id,
  //       data: {
  //         status: "approver",
  //         ...obj,
  //         remarks: value,
  //       },
  //     });

  //     try {
  //       await Promise.all(
  //         selectedData.map((el: any) =>
  //           disbursementsPaymentChecker({ id: el }).unwrap()
  //         )
  //       );
  //     } catch (error: any) {
  //       toastMessage.error(
  //         error?.data?.data?.message || "Failed to send payment data"
  //       );
  //     } finally {
  //       setIsLoading(false);
  //     }

  //     return;
  //   }

  //   if (handleChecked() === "CHECK") {
  //     updateBatch({
  //       id,
  //       data: {
  //         // status: "approver",
  //         ...obj,
  //         remarks: value,
  //       },
  //     });

  //     return;
  //   }

  //   // if (!selectedData?.length) return;

  //   // setIsLoading(true);

  //   // try {
  //   //   await Promise.all(
  //   //     selectedData.map((el: any) =>
  //   //       disbursementsPaymentChecker({ id: el }).unwrap()
  //   //     )
  //   //   );
  //   // } catch (error: any) {
  //   //   toastMessage.error(
  //   //     error?.data?.data?.message || "Failed to send payment data"
  //   //   );
  //   // } finally {
  //   //   setIsLoading(false);
  //   // }
  // };
  const totalAmount = vendorDetails?.data?.data?.reduce(
    (sum, item) => sum + Number(item?.amount || 0),
    0
  );

  const walletBalance = balanceCheck?.data?.data?.Amount;

  const isWalletSufficient = Number(walletBalance) >= (totalAmount ?? 0);

  const handleMackerSubmit = () => {
    const newData = {
      disbursementIds: selectedData,
    };
    // console.log("newData", newData);
    sentToCheckerVendor({
      data: newData,
    });
  };

  const handleSentToChecker = (value: string) => {
    const newData = {
      remarks: value,
    };

    vendorPaymentsSentToChecker({
      id: Number(id),
      data: newData,
    });
  };

  const handlePoolChecked = () => {
    if (roleCheck !== "Approver") {
      return null;
    }

    // if (singleBatchData?.data?.status !== "approver") {
    //   return null;
    // }

    const poolName = userPoolProjectData?.data?.poolName;

    console.log("poolName", poolName);

    if (
      singleBatchData?.data?.numberOfPollA ===
        Number(singleBatchData?.data?.numberOfPoolACheck) &&
      singleBatchData?.data?.numberOfPollB ===
        Number(singleBatchData?.data?.numberOfPoolBCheck)
    ) {
      // payment korte parbe
      return "PAYMENT";
    } else if (poolName === "Pool A" && singleBatchData?.data?.numberOfPollA) {
      if (
        singleBatchData?.data?.numberOfPollA >
        Number(singleBatchData?.data?.numberOfPoolACheck)
      ) {
        // send to approve korte parbe
        return "APPROVE";
      }
      console.log("Condition 1");
      return null;
    } else if (poolName === "Pool B" && singleBatchData?.data?.numberOfPollB) {
      if (
        singleBatchData?.data?.numberOfPollB >
        Number(singleBatchData?.data?.numberOfPoolBCheck)
      ) {
        // send to approve korte parbe
        return "APPROVE";
      }
      console.log("Condition 2");
      return null;
    }
    console.log("Condition 3");
    return null;
  };

  const handleSendToApprove = async () => {
    const poolName = userPoolProjectData?.data?.poolName;
    let obj = {};
    if (poolName === "Pool A") {
      obj = {
        numberOfPoolACheck:
          (singleBatchData?.data?.numberOfPoolACheck || 0) + 1,
      };
    }
    if (poolName === "Pool B") {
      obj = {
        numberOfPoolBCheck:
          (singleBatchData?.data?.numberOfPoolBCheck || 0) + 1,
      };
    }

    console.log("APPROVE comment", comment);

    if (handlePoolChecked() === "APPROVE") {
      updateBatch({
        id: id,
        data: {
          status: "approver",
          remarks: comment,
          ...obj,
        },
      });

      return;
    }

    if (!selectedData?.length) return;

    setIsLoading(true);

    try {
      await Promise.all(
        selectedData.map((el: any) =>
          vendorPaymentsSentToChecker({ id: el }).unwrap()
        )
      );
    } catch (error: any) {
      toastMessage.error(
        error?.data?.data?.message || "Failed to send payment data"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleBatchReject = (value: string) => {
    const newData = {
      batchId: id,
      current_stage: roleCheck?.toLocaleLowerCase(),
      remarks: value,
    };
    batchRejectVendor({
      data: newData,
    });
  };

  const getModalTitle = () => {
    if (
      openConfirmSubmitModal.type === "CONFIRM_SUBMIT" ||
      openConfirmMackerModal.type === "CONFIRM_SUBMIT_MACKER"
    )
      return "Payment Request Submitted Successfully!";
    if (openConfirmPaymentModal.type === "CONFIRM_PAYMENT")
      return "Payment Successfully Done!";
    if (openConfirmRejectModal.type === "CONFIRM_REJECT")
      return "Payment Request Rejected Successfully!";
    return "";
  };

  const handleOkay = () => {
    handleConfirmMackerClose();
    handleSuccessClose();
    handleConfirmSubmitClose();
    handleConfirmRejectClose();
  };

  const selectedRows =
    vendorDetails?.data?.data?.filter((row: any) =>
      selectedData.includes(row.id)
    ) || [];
  const totalSelectedAmount = selectedRows.reduce(
    (sum: number, row: any) => sum + (Number(row.amount) || 0),
    0
  );

  const exportToExcel = () => {
    const rawData = vendorDetails?.data?.data;

    // Column headers
    const columns = {
      "Vendor ID": "",
      "Vendor Name": "",
      "Invoice No": "",
      Amount: "",
      "Wallet Number": "",
      "Bank Name": "",
      "Account Title": "",
      "Account Number": "",
      "Branch Name": "",
      "Routing Number": "",
      "Transaction ID": "",
      "Transaction Date": "",
      Narration: "",
      "Current Stage": "",
      Status: "",
    };

    let formattedData: any[] = [];

    if (rawData?.length) {
      formattedData = rawData.map((item: any) => ({
        "Vendor ID": item?.vendor_id || "-",
        "Vendor Name": item?.vendor_name || "-",
        "Invoice No": item?.invoiceNo || "-",
        Amount: item?.amount || "-",
        "Wallet Number": item?.wallet_name || "-",
        "Bank Name": item?.bank_name || "-",
        "Account Title": item?.account_title || "-",
        "Account Number": item?.account_number || "-",
        "Branch Name": item?.branch_name || "-",
        "Routing Number": item?.routing_number || "-",
        "Transaction ID": item?.transaction_id || "-",
        "Transaction Date": item?.transaction_date || "-",
        Narration: item?.narration || "-",
        "Current Stage": item?.current_stage || "-",
        Status: item?.status || "-",
      }));
    } else {
      // Push one empty row to ensure headers appear
      formattedData.push(columns);
    }

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employees");

    XLSX.writeFile(
      workbook,
      `Vendor_Payment_${singleBatch?.batchName}_${rawData?.length}.xlsx`
    );
  };

  useEffect(() => {
    if (sentToCheckerIsSuccess) {
      handleSuccessOpen({ type: "SUCCESS" });
      setSelectedData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentToCheckerIsSuccess]);

  useEffect(() => {
    if (isPaymentCheckerSuccess && roleCheck === "Checker") {
      handleSuccessOpen({ type: "SUCCESS" });
      setSelectedData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaymentCheckerSuccess, roleCheck === "Checker"]);

  useEffect(() => {
    if (isPaymentCheckerSuccess && handlePoolChecked() === "APPROVE") {
      console.log("isPaymentCheckerSuccess comment", comment);
      updateBatch({
        id: id,
        data: {
          status: "approver",
          remarks: comment,
        },
      });

      toastMessage.success("Payment data sent successfully");
      setSelectedData([]);
      setComment("");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaymentCheckerSuccess, handlePoolChecked() === "APPROVE"]);

  useEffect(() => {
    if (isUpdateBatchSuccess) {
      handleClose();
      setComment("");
      setSelectedData([]);
      toastMessage.success("Payment data sent successfully");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateBatchSuccess]);

  useEffect(() => {
    if (batchRejectIsSuccess) {
      handleSuccessOpen({ type: "SUCCESS" });
      setComment("");

      // toastMessage.success("Payment data sent successfully");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchRejectIsSuccess]);

  return (
    <>
      {open?.type === "CONFIRM" && (
        <ConfirmModalLayout
          title="Send to Approver"
          subTitle="Are you sure you want to send payment data to approver"
          buttonName="Send"
          handleButton={handleSendToApprove}
          open={open?.open}
          handleClose={handleClose}
          action
          buttonBgDeep
          loading={isUpdateBatchLoading}
        >
          <div>
            <div className="mb-2 text-[#5E718D]">Comment</div>
            <Input
              placeholder="Enter comment"
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
        </ConfirmModalLayout>
      )}

      {addWalletOpen?.type === "ADD" || addWalletOpen?.type === "UPDATE" ? (
        <AddWalletModal
          open={addWalletOpen?.open}
          handleClose={addWalletHandleClose}
          data={companyDetails?.data?.wallet_number}
          type={addWalletOpen?.type}
        />
      ) : null}

      {openConfirmSubmitModal.type === "CONFIRM_SUBMIT" && (
        <ConfirmActionModal
          title="Are you sure you want to submit payment request?"
          handleClose={handleConfirmSubmitClose}
          open={openConfirmSubmitModal.open}
          isLoading={paymentCheckerIsLoading}
          isSubmit
          handleCheckerSubmit={(val) => handleSentToChecker(String(val))}
        />
      )}

      {openConfirmMackerModal.type === "CONFIRM_SUBMIT_MACKER" && (
        <ConfirmActionModal
          title="Are you sure you want to submit payment request?"
          handleClose={handleConfirmMackerClose}
          handleMackerSubmit={handleMackerSubmit}
          open={openConfirmMackerModal.open}
          isLoading={sentToCheckerIsLoading}
          isYesSubmit
        />
      )}

      {openConfirmPaymentModal.type === "CONFIRM_PAYMENT" && (
        <ConfirmActionModal
          title="Are you sure you want to submit payment request?"
          handleClose={handleConfirmPaymentClose}
          open={openConfirmPaymentModal.open}
          isPayment
        />
      )}

      {openConfirmRejectModal.type === "CONFIRM_REJECT" && (
        <ConfirmActionModal
          title="Are you sure you want to reject payment request?"
          handleClose={handleConfirmRejectClose}
          open={openConfirmRejectModal.open}
          handleBatchRejectSubmit={(val) => handleBatchReject(String(val))}
          isLoading={batchRejectIsLoading}
          isReject
        />
      )}

      {openSuccessModal.type === "SUCCESS" && (
        <SuccessModal
          open={openSuccessModal?.open}
          handleClose={handleSuccessClose}
          handleOkay={handleOkay}
          data={openSuccessModal?.data}
          title={getModalTitle()}
        />
      )}

      {open?.type === "CONFIRM_FOR_APPROVAL" && (
        <PinVerifyModal
          open={open?.open}
          handleClose={handleClose}
          refetch={refetch}
          isPaymentLoading={isSalaryPaymentLoading || isUpdateBatchLoading}
          isPaymentSuccess={isSalaryPaymentSuccess}
          paymentApi={(data) => {
            vendorsPaymentWithOtp(data);
          }}
          paymentMessage={
            salaryPaymentData?.message || "Payment approved successfully"
          }
        />
        // <ConfirmModalLayout
        //   title="Approve"
        //   subTitle="Are you sure you want to approve payment"
        //   buttonName="Approved"
        //   handleButton={handleApprover}
        //   open={open?.open}
        //   handleClose={() => {
        //     handleClose();
        //     refetch();
        //     setSelectedData([]);
        //   }}
        //   action
        //   buttonBgDeep
        //   loading={isLoading}
        // >
        //   <Form.Item
        //     // label="Pin Number"
        //     name="pn"
        //     rules={[
        //       {
        //         required: true,
        //         message: "Please enter pin number",
        //       },
        //       {
        //         pattern: /^\d{6,}$/,
        //         message: "Pin number must be at least 6 digits",
        //       },
        //     ]}
        //   >
        //     <Input
        //       placeholder="Enter pin number"
        //       onChange={(e) => setPinNumber(e.target.value)}
        //     />
        //   </Form.Item>
        // </ConfirmModalLayout>
      )}

      <div>
        <div>
          {paymentStatus === "true" && (
            <>
              <Card style={{ width: "100%", marginBottom: "25px" }}>
                {isCompanyDetailsLoading ? (
                  <Loader />
                ) : user?.data?.company_id &&
                  (roleCheck === "Admin" ||
                    roleCheck === "Approver" ||
                    role === "SUPER_ADMIN") ? (
                  <>
                    <div className="flex justify-between items-center">
                      <div className="flex justify-start items-center gap-2">
                        <img
                          src={companyDetails?.data?.logo || AvatarImage}
                          alt=""
                          className="w-14 h-14 rounded-full"
                        />
                        <div className="ml-2">
                          <div className="text-blue-500 text-lg font-semibold leading-normal mb-0.5">
                            {companyDetails?.data?.company_name}
                          </div>
                          {companyDetails?.data?.wallet_number ? (
                            <p className="text-blue-500 text-sm font-medium leading-tight flex items-center gap-3">
                              <span>
                                Wallet No:{" "}
                                {companyDetails?.data?.wallet_number || "N/A"}{" "}
                              </span>
                              {roleCheck === "Admin" ||
                              roleCheck === "Approver" ||
                              role === "SUPER_ADMIN" ? (
                                <span
                                  className="cursor-pointer"
                                  onClick={() =>
                                    addWalletHandleOpen({ type: "UPDATE" })
                                  }
                                >
                                  <EditIcon />
                                </span>
                              ) : null}
                            </p>
                          ) : roleCheck === "Admin" ||
                            roleCheck === "Approver" ||
                            role === "SUPER_ADMIN" ? (
                            <Button
                              icon={<AddIcon />}
                              onClick={() =>
                                addWalletHandleOpen({ type: "ADD" })
                              }
                              className="mt-2"
                              type="primary"
                              size="small"
                            >
                              Submit
                            </Button>
                          ) : null}
                        </div>
                      </div>

                      {isBalanceCheckLoading ? (
                        <Spin />
                      ) : user?.data?.company_id ? (
                        <BalanceSlide>
                          {addCommaInNumber(
                            Math.round(balanceCheck?.data?.data?.Amount || 0)
                          )}
                          BDT
                        </BalanceSlide>
                      ) : null}
                    </div>

                    {/* <div className="self-stretch h-0 outline-1 outline-dashed outline-offset-[-0.50px] outline-zinc-200 mt-5 mb-10"></div> */}
                  </>
                ) : null}

                {roleCheck === "Maker" || roleCheck === "Checker" ? (
                  <div className="mb-3  flex justify-end">
                    {isBalanceCheckLoading ? (
                      <Spin />
                    ) : user?.data?.company_id ? (
                      <BalanceSlide>
                        {addCommaInNumber(
                          Math.round(balanceCheck?.data?.data?.Amount || 0)
                        )}
                        BDT
                      </BalanceSlide>
                    ) : null}
                  </div>
                ) : null}
              </Card>
            </>
          )}
        </div>

        <div className="flex justify-between items-center mb-3">
          <div className="justify-start text-zinc-700 text-xl font-semibold font-['Inter'] leading-loose">
            Pending List
          </div>
          <Button
            disabled={!vendorDetails?.data?.data?.length}
            onClick={exportToExcel}
            className="h-9 px-3.5 py-2 !bg-white rounded-md outline outline-1 outline-offset-[-1px] outline-blue-500 inline-flex justify-center items-center gap-2.5 overflow-hidden disabled:cursor-not-allowed"
          >
            <span className="text-blue-600 text-sm font-semibold">
              Download
            </span>
          </Button>
        </div>

        <CustomTable
          loading={isVendorLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={vendorDetails?.data?.count}
          dataSource={addKeyInArray(vendorDetails?.data?.data)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          {...(roleCheck === "Maker"
            ? {
                rowSelection: {
                  type: "checkbox",
                  selectedRowKeys: selectedData,
                  onChange: (key) => {
                    setSelectedData(key as string[]);
                  },
                  getCheckboxProps: (record) => {
                    return {
                      disabled:
                        record?.status === "invited" ||
                        (record?.current_stage === "checker" &&
                          roleCheck === "Maker"),
                    };
                  },
                  columnWidth: 40,
                },
              }
            : {})}
          filterSection={
            <>
              {roleCheck === "Maker" && (
                <div className="px-3 py-1 bg-violet-50 rounded-[100px] inline-flex justify-center items-center gap-2.5">
                  <div className="justify-start text-blue-600 text-base font-medium">
                    Total Amount: ৳{" "}
                    {Number(totalSelectedAmount)
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "0"}
                  </div>
                </div>
              )}

              {roleCheck === "Checker" && (
                <div className="px-3 py-1 bg-violet-50 rounded-[100px] inline-flex justify-center items-center gap-2.5">
                  <div className="justify-start text-blue-600 text-base font-medium">
                    Total Amount: ৳{" "}
                    {Number(totalAmount)
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "0"}
                  </div>
                </div>
              )}

              {roleCheck === "Approver" && (
                <>
                  <div className="px-3 py-1 bg-violet-50 rounded-[100px] inline-flex justify-center items-center gap-2.5">
                    <div className="justify-start text-blue-600 text-base font-medium">
                      Total Amount: ৳{" "}
                      {Number(totalAmount)
                        .toFixed(2)
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "0"}
                    </div>
                  </div>

                  {isBalanceCheckLoading ? null : (
                    <>
                      {!isWalletSufficient && (
                        <div className="mt-2 w-full">
                          <Alert
                            message="Your total amount is bigger from your wallet balance"
                            type="warning"
                            showIcon
                            closable
                          />
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              <Input
                suffix={<SearchIcon />}
                placeholder="Search ID, Name, Wallet, Ac. Number"
                style={{ width: 317 }}
                allowClear
                onChange={handleSearch}
              />

              {roleCheck === "Maker" && (
                <Button
                  onClick={() =>
                    handleConfirmMackerOpen({ type: "CONFIRM_SUBMIT_MACKER" })
                  }
                  disabled={selectedData.length === 0}
                  className="cursor-pointer h-9 px-3.5 py-2 !bg-orange-500 rounded-md inline-flex justify-center items-center gap-2.5"
                >
                  <span className="!text-white text-sm font-semibold">
                    Send to next
                  </span>
                </Button>
              )}

              {roleCheck === "Checker" && (
                <>
                  <Button
                    onClick={() =>
                      handleConfirmRejectOpen({ type: "CONFIRM_REJECT" })
                    }
                    disabled={
                      CheckButtonForVendorPaymentsStatusData?.data?.status ===
                      false
                    }
                    className="cursor-pointer h-9 px-3.5 py-2 !bg-red-50 rounded-md outline outline-1 outline-offset-[-1px] outline-red-300 inline-flex justify-center items-center gap-2.5"
                  >
                    <span className="text-red-500 text-sm font-semibold">
                      Reject
                    </span>
                  </Button>

                  <Button
                    onClick={() =>
                      handleConfirmSubmitOpen({ type: "CONFIRM_SUBMIT" })
                    }
                    disabled={
                      CheckButtonForVendorPaymentsStatusData?.data?.status ===
                      false
                    }
                    className="cursor-pointer h-9 px-3.5 py-2 !bg-orange-500 rounded-md inline-flex justify-center items-center gap-2.5"
                  >
                    <span className="!text-white text-sm font-semibold">
                      Send to next
                    </span>
                  </Button>
                </>
              )}

              {/* {roleCheck === "Approver" && (
                <Button
                  onClick={() =>
                    handleConfirmPaymentOpen({ type: "CONFIRM_PAYMENT" })
                  }
                  className="cursor-pointer h-9 px-3.5 py-2 !bg-orange-500 rounded-md inline-flex justify-center items-center gap-2.5"
                >
                  <span className="!text-white text-sm font-semibold">
                    Payment
                  </span>
                </Button>
              )} */}

              {handlePoolChecked() === "APPROVE" && (
                <>
                  <Button
                    onClick={() =>
                      handleConfirmRejectOpen({ type: "CONFIRM_REJECT" })
                    }
                    disabled={
                      CheckButtonForVendorPaymentsStatusData?.data?.status ===
                      false
                    }
                    className="cursor-pointer h-9 px-3.5 py-2 bg-red-50 rounded-md outline outline-1 outline-offset-[-1px] outline-red-300 inline-flex justify-center items-center gap-2.5"
                  >
                    <span className="text-red-500 text-sm font-semibold">
                      Reject
                    </span>
                  </Button>

                  <Button
                    className="middle-button min-w-[150px]"
                    type="primary"
                    disabled={
                      vendorDetails?.data?.data?.length === 0 ||
                      isWalletSufficient === false
                    }
                    onClick={() => handleOpen({ type: "CONFIRM" })}
                    loading={isUpdateBatchLoading}
                  >
                    Approve
                  </Button>
                </>
              )}

              {handlePoolChecked() === "PAYMENT" && (
                <Button
                  className="middle-button min-w-[150px]"
                  type="primary"
                  // disabled={
                  //   !selectedData?.length || !!userPerformBatchData?.data
                  // }

                  disabled={
                    CheckButtonForVendorPaymentsStatusData?.data?.status ===
                    false
                  }
                  onClick={() => handleOpen({ type: "CONFIRM_FOR_APPROVAL" })}
                  loading={isSalaryPaymentLoading}
                >
                  Payment
                </Button>
              )}
            </>
          }
        />
      </div>
    </>
  );
}
